import {
  Box,
  Grid,
  Link,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { isEmpty } from "lodash";

import {
  DisciplineContainerStyle,
  DisciplineTableCell,
  DocumentDetailsTableCell,
  DocumentName,
  TableMain,
  TableMainHead,
  TableMainHeadRow,
} from "../../AuthorizationDetails/Style/DisciplineStyle";
import fontWeight from "../../../styles/mui/fontWeight";
import { NO_RECORD_FOUND } from "../../../constants/tostmsg";
import colors from "../../../styles/colors/colors";
import { AuthHistoryHeaderLabel } from "../../../constants/Document";
import { flexAlignCentre } from "../../../styles/mui/styles/display";
import CustomTablePagination from "../../../App/components/CustomTablePagination";
import { AuthorizationHistoryResponse } from "../../../models/Api/Authorization";
import {
  AuthAndEligibilityHistoryPayload,
  AuthorizationHistory,
  PaginationPayload,
} from "../../../models/Authorization";
import { DefaultPaginationData } from "../../../constants/Disciplines";
import { formatDate } from "../../../utils/DateTime";
import { DateFormats } from "../../../constants/Date";
import { useNavigate, useParams } from "react-router-dom";
import { OuterGrid } from "../Style";
import { AUTH_ID, AUTHDESC } from "../../../constants/FunctionalUtils";
import { encryptId, decryptId } from "../../../utils";
export interface PropsFromState {
  authHistoryData: AuthorizationHistoryResponse;
}
export interface PropsFromDispatch {
  getAuthHistory: (payload: AuthAndEligibilityHistoryPayload) => void;
}
type Props = PropsFromState & PropsFromDispatch;
const AuthHistoryUI: React.FC<Props> = ({
  authHistoryData,
  getAuthHistory,
}: Props) => {
  const { response } = authHistoryData;
  const [paginationData, setPaginationData] = useState<PaginationPayload>(
    DefaultPaginationData
  );
  const [authHistoryRecords, setAuthHistoryRecords] = useState<
    AuthorizationHistory[] | null
  >(null);
  const [decryptedPatientId, setDecryptedPatientId] = useState<number | null>(
    null
  );
  const navigate = useNavigate();
  const { patientId } = useParams();

  useEffect(() => {
    const loadPatientId = async () => {
      if (!patientId) {
        setDecryptedPatientId(null);
        return;
      }

      const decrypted = await decryptId(patientId);
      const parsed = Number(decrypted);
      setDecryptedPatientId(Number.isFinite(parsed) ? parsed : null);
    };

    void loadPatientId().catch(() => setDecryptedPatientId(null));
  }, [patientId]);

  const handlePagination = (paginationData: PaginationPayload) => {
    const payload = {
      ...paginationData,
      patientId: decryptedPatientId ?? 0,
      contains: true,
      sortColumn: AUTH_ID,
      sortOrder: AUTHDESC,
    };
    setPaginationData(paginationData);
    getAuthHistory(payload);
  };

  useEffect(() => {
    if (response && response.authorizationHistory) {
      setAuthHistoryRecords(response.authorizationHistory);
    }
  }, [response]);

  useEffect(() => {
    if (decryptedPatientId !== null) {
      const payload = {
        pageNo: 1,
        pageSize: 10,
        patientId: decryptedPatientId,
        contains: true,
        sortColumn: AUTH_ID,
        sortOrder: AUTHDESC,
      };
      getAuthHistory(payload);
    }
  }, [decryptedPatientId, getAuthHistory]);
  const handleView = async (authId: number) => {
    const encryptedAuthId = await encryptId(authId);
    navigate(`/authorization-details/${encryptedAuthId}`);
  };
  const validation = () => {
    return (
      <TableRow>
        <TableCell colSpan={14}>
          <Box display="flex" justifyContent={"center"}>
            <Typography
              variant="subtitle2"
              fontWeight={fontWeight.Weight[4]}
              sx={DisciplineTableCell}
            >
              {NO_RECORD_FOUND}
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  const documentTableCell = (value: string | number, date?: boolean) => {
    return (
      <>
        <TableCell sx={DocumentDetailsTableCell}>
          <Typography
            variant="subtitle1"
            fontWeight={date ? fontWeight.Weight[4] : fontWeight.Weight[5]}
            color={colors.fonts[4]}
          >
            {value}
          </Typography>
        </TableCell>
      </>
    );
  };
  return (
    <Grid container>
      <Grid size={12}>
        <Grid container>
          <Grid size={12}>
            <Typography
              variant="h4"
              fontWeight={fontWeight.Weight[7]}
              color={colors.fonts[1]}
              p={"1rem 0"}
            >
              AUTHORIZATION HISTORY:
            </Typography>
          </Grid>
          <Grid container sx={OuterGrid}>
            <Grid size={12}>
              <TableContainer sx={DisciplineContainerStyle} component={Paper}>
                <TableMain>
                  <TableMainHead>
                    <TableMainHeadRow>
                      {AuthHistoryHeaderLabel &&
                        AuthHistoryHeaderLabel.map((cell) => (
                          <TableCell width={cell.width} key={cell.label}>
                            <Typography
                              variant="subtitle2"
                              fontWeight={fontWeight.Weight[6]}
                              color={colors.fonts[4]}
                            >
                              {cell.label}
                            </Typography>
                          </TableCell>
                        ))}
                    </TableMainHeadRow>
                  </TableMainHead>
                  <TableBody>
                    {authHistoryRecords && authHistoryRecords.length > 0
                      ? authHistoryRecords.map((row: AuthorizationHistory) => {
                          return (
                            <TableRow key={row.authId}>
                              <TableCell>
                                <Box sx={flexAlignCentre}>
                                  <Typography
                                    variant="h5"
                                    fontWeight={fontWeight.Weight[6]}
                                    color={colors.primary.main}
                                    pt={"0.3rem"}
                                  >
                                    <Link
                                      variant="h5"
                                      fontWeight={fontWeight.Weight[4]}
                                      color={colors.fonts[18]}
                                      onClick={() => {
                                        void handleView(row.authId);
                                      }}
                                      data-testid={`AuthHistory-${row.authId}`}
                                    >
                                      <Typography
                                        variant="subtitle1"
                                        fontWeight={fontWeight.Weight[5]}
                                        color={colors.fonts[18]}
                                        sx={DocumentName}
                                      >
                                        {row.authorizationNumber}
                                      </Typography>
                                    </Link>
                                  </Typography>
                                </Box>
                              </TableCell>
                              {documentTableCell(row.healthPlan)}
                              {documentTableCell(row.subscriberId)}
                              {documentTableCell(isEmpty(row.referralStatusName) ? "Incomplete": row.referralStatusName)}
                              {documentTableCell(row.serviceProviderNpi)}
                              {documentTableCell(
                                formatDate(
                                  row.admitDate,
                                  DateFormats.MM_DD_YYYY
                                )
                              )}
                              {documentTableCell(
                                formatDate(
                                  row.dischargeDate,
                                  DateFormats.MM_DD_YYYY
                                )
                              )}
                              {documentTableCell(row.totalApproved)}
                              {documentTableCell(row.sn)}
                              {documentTableCell(row.pt)}
                              {documentTableCell(row.ot)}
                              {documentTableCell(row.st)}
                              {documentTableCell(row.msw)}
                              {documentTableCell(row.hha)}
                            </TableRow>
                          );
                        })
                      : validation()}
                  </TableBody>
                </TableMain>
              </TableContainer>
            </Grid>
            <Grid size={12}>
              <CustomTablePagination
                totalRecords={
                  response && response?.totalRows ? response?.totalRows : 0
                }
                handlePagination={handlePagination}
                paginationData={paginationData}
                paginationInjectedTo="discipline-list"
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default AuthHistoryUI;
